import {CoreConfig, CoreLogger, createDMCore, DocumentManager} from '@wix/document-manager-core'
import type {
    DataFixer,
    Experiment,
    PageListWithMasterAndMainPage,
    DSConfig,
    ValidationService
} from '@wix/document-services-types'
import * as santaDataFixer from '@wix/santa-data-fixer'
import _ from 'lodash'
import {createConfig, isCEditOpen} from './createConfig'
import {loadExtensions} from './extensionLoader'
import {DefaultFetchPagesFacade} from './initialize/fetchPages'
import {InitResult, initialize, InitParams} from './initialize/mainInitialization'
import type {
    DocumentServicesModel,
    DocumentServicesModelForServer,
    RendererModel,
    RendererModelForServer
} from './initialize/modelTypes'
import type {BootstrapConfig} from './types'
import {ReportableError} from '@wix/document-manager-utils'
import {
    createDataMigrationRunner,
    DataMigrationRunner,
    defaultPageMigrations,
    defaultRunOncePerSite,
    migratorsVersioningConfig as _migratorsVersioningConfig,
    type MigratorsSettings,
    type SantaDataFixersSettings
} from '@wix/document-manager-extensions'
import {newSchemaService} from '@wix/document-services-json-schemas'
import {validationService as validationServicePackage} from '@wix/dm-validation'
import {DefaultFetchFragmentsFacade} from './initialize/fetchFragments'
export type {FetchPagesToDalFunction} from './initialize/mainInitialization'

export {isCEditOpen}
export type EnvironmentContext = Record<string, any>

const buildCoreConfig = (
    config: DSConfig,
    experimentInstance: Experiment,
    logger: CoreLogger,
    schemaService: any,
    validationService?: ValidationService,
    tracking?: Function
): CoreConfig => {
    return {
        experimentInstance,
        logger,
        tracking,
        schemaService,
        validationService,
        disableCommonConfig: config.disableCommonConfig,
        transactionActionsLimit: config.transactionActionsLimit,
        signatureSeed: config.signatureSeed,
        undoRedoConfig: config.undoRedoConfig,
        supportsUsingPresetVariants: config.supportsUsingPresetVariants,
        dontCollectFixerVersionData: config.dontCollectFixerVersionData,
        minNumOfPagesToCalculateRenderHints: config.minNumOfPagesToCalculateRenderHints,
        enableWidgetInRepeater: config.enableWidgetInRepeater,
        supportedFragments: config.supportedFragments,
        withoutViewer: config.withoutViewer,
        enforceStrictValidationEverywhere: config.enforceStrictValidationEverywhere,
        strictBlacklistBaselineForEmployees: config.strictBlacklistBaselineForEmployees,
        enforceNoWhitelistDalStrictSchemaValidation: config.enforceNoWhitelistDalStrictSchemaValidation,
        loadingPartialPages: config.loadingPartialPages || config.lazyLoadPages
    }
}

/**
 * Create a working document Manager which includes the core, registered extensions and initialized state
 * the Document Manager is ready to accept set operations. Public APIs are not initialized yet
 */
const createDocumentManager = (
    config: DSConfig,
    experimentInstance: Experiment,
    logger: CoreLogger,
    schemaService: any,
    environmentContext: EnvironmentContext,
    validationService?: ValidationService
): DocumentManager => {
    const coreConfig = buildCoreConfig(
        config,
        experimentInstance,
        logger,
        schemaService,
        validationService,
        _.get(environmentContext, ['trackingFn'])
    )
    const core = createDMCore(coreConfig)
    loadExtensions(core, config, environmentContext)
    return core
}

export type AnyRendererModel = RendererModelForServer | RendererModel

export interface RendererModelBuilderForHost {
    getRendererModel(): Promise<AnyRendererModel>
}

export interface Models {
    serviceTopology: Record<string, any>
    documentServicesModel: DocumentServicesModelForServer | DocumentServicesModel
}

type InitFunc = (initObj: InitParams) => Promise<InitResult>

export interface CreateHostArgs {
    models: Models
    partialPages?: string[]
    config: BootstrapConfig
    experimentInstance: Experiment
    logger: CoreLogger
    pageList: PageListWithMasterAndMainPage
    rendererModelBuilder: RendererModelBuilderForHost
    environmentContext?: EnvironmentContext
    dataFixer?: DataFixer
    dataMigrationRunner?: DataMigrationRunner
    initFunc?: InitFunc
    santaDataFixersSettings?: SantaDataFixersSettings
    migratorsSettings?: MigratorsSettings
}

export interface Host {
    readonly documentManager: DocumentManager
    readonly config: DSConfig
    _dsInitTimeoutHandler?: any //should be number
    runInitializers(): Promise<InitResult>
    environmentContext: EnvironmentContext
}

export const createHost = ({
    models,
    partialPages = [],
    config: bootstrapConfig,
    experimentInstance,
    logger,
    environmentContext: envContext = {},
    dataFixer = santaDataFixer,
    dataMigrationRunner = createDataMigrationRunner(),
    initFunc,
    pageList,
    rendererModelBuilder,
    santaDataFixersSettings = {
        santaDataFixerPlugins: santaDataFixer.getPlugins(),
        santaDataFixersVersioningConfig: santaDataFixer.getFixerVersioningConfig()
    },
    migratorsSettings = {
        pageMigrators: defaultPageMigrations,
        siteMigrators: defaultRunOncePerSite,
        migratorsVersioningConfig: _migratorsVersioningConfig
    }
}: CreateHostArgs): Host => {
    if (!experimentInstance.isOpen('dm_forceConfigName') && !bootstrapConfig.configName) {
        // Remove this whole block when dm_forceConfigName is merged
        logger.captureError(
            new ReportableError({
                message: 'Missing a configName parameter',
                errorType: 'missingConfigNameError'
            })
        )
        bootstrapConfig.configName = 'fullFunctionality'
    }

    const environmentContext: EnvironmentContext = {
        fetchPagesFacade: _.get(envContext, ['fetchPagesFacade']) ?? new DefaultFetchPagesFacade(),
        fetchFragmentsFacade: _.get(envContext, ['fetchFragmentsFacade']) ?? new DefaultFetchFragmentsFacade(),
        ...envContext
    }
    // Use pre-built dsConfig if provided, otherwise create it
    const config: DSConfig = createConfig(bootstrapConfig, experimentInstance)
    const documentManager = createDocumentManager(
        config,
        experimentInstance,
        logger,
        environmentContext.schemaService ?? newSchemaService.getInstanceByExperiments(experimentInstance, config),
        environmentContext,
        environmentContext.validationService ?? validationServicePackage.staticInstance
    )

    const runInitializers = async (): Promise<InitResult> => {
        const {serviceTopology, documentServicesModel} = models
        const init = initFunc ?? initialize
        const initResult = await init({
            documentManager,
            partialPages: pagesToLoad(config, partialPages, pageList),
            dataFixer,
            dataMigrationRunner,
            serviceTopology,
            documentServicesModel,
            rendererModelBuilder,
            config,
            logger,
            fetchFn: environmentContext.fetchFn,
            trackingFn: environmentContext.trackingFn,
            syncTrackingFn: environmentContext.syncTrackingFn,
            experimentInstance,
            fetchPagesFacade: environmentContext.fetchPagesFacade,
            pageFetchFallbackFn: environmentContext.pageFetchFallbackFn,
            pageList,
            santaDataFixersSettings,
            migratorsSettings
        })
        await documentManager.initialize()
        return initResult
    }

    return {
        documentManager,
        runInitializers,
        config,
        environmentContext
    }
}

function pagesToLoad(config: DSConfig, partialPages: string[], pageList: PageListWithMasterAndMainPage) {
    if (!_.isEmpty(partialPages)) {
        return partialPages
    }
    if (config.lazyLoadPages) {
        return [pageList.mainPageId, 'masterPage']
    }
    return [] // This means all pages in the partial pages API
}
